import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Search, Edit, Trash2, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SupplierForm } from './SupplierForm';
import { exportSuppliersToExcel, parseExcelFile, convertExcelToSupplierData } from '@/utils/excelUtils';
import { suppliersService } from '@/services/suppliersService';

export const SuppliersList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: suppliers, isLoading, refetch } = useQuery({
    queryKey: ['suppliers', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('suppliers')
        .select('*')
        .eq('is_active', true)
        .order('display_name');

      if (searchTerm) {
        query = query.ilike('display_name', `%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Query for all suppliers (for export)
  const { data: allSuppliers } = useQuery({
    queryKey: ['all-suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('display_name');
      
      if (error) throw error;
      return data;
    }
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (supplierId: string) => {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', supplierId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Supplier deleted successfully" });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting supplier",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (supplierData: any[]) => {
      const results = { updated: 0, created: 0, errors: [] as string[] };
      
      for (const supplier of supplierData) {
        try {
          if (supplier.id) {
            // Update existing supplier
            await suppliersService.updateSupplier(supplier);
            results.updated++;
          } else {
            // Create new supplier
            const { id, ...createData } = supplier;
            await suppliersService.createSupplier(createData);
            results.created++;
          }
        } catch (error: any) {
          results.errors.push(`Error processing ${supplier.display_name}: ${error.message}`);
        }
      }
      
      return results;
    },
    onSuccess: (results) => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['all-suppliers'] });
      
      let message = '';
      if (results.updated > 0) message += `Updated ${results.updated} suppliers. `;
      if (results.created > 0) message += `Created ${results.created} suppliers. `;
      
      toast({ 
        title: "Excel update completed", 
        description: message.trim() || "No changes were made",
        variant: results.errors.length > 0 ? "destructive" : "default"
      });
      
      if (results.errors.length > 0) {
        console.error('Bulk update errors:', results.errors);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error updating suppliers",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleEdit = (supplier: any) => {
    setEditingSupplier(supplier);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['all-suppliers'] });
    setEditingSupplier(null);
  };

  const handleFormClose = (open: boolean) => {
    setFormOpen(open);
    if (!open) {
      setEditingSupplier(null);
    }
  };

  const handleExport = () => {
    if (!allSuppliers || allSuppliers.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no suppliers to export",
        variant: "destructive"
      });
      return;
    }
    
    exportSuppliersToExcel(allSuppliers, `suppliers_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast({ title: "Excel file downloaded successfully" });
  };

  const handleExcelUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const excelData = await parseExcelFile(file);
      const supplierData = convertExcelToSupplierData(excelData);
      
      if (supplierData.length === 0) {
        toast({
          title: "No valid data found",
          description: "The Excel file does not contain valid supplier data",
          variant: "destructive"
        });
        return;
      }

      bulkUpdateMutation.mutate(supplierData);
    } catch (error: any) {
      toast({
        title: "Error reading Excel file",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Suppliers</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={!allSuppliers || allSuppliers.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={bulkUpdateMutation.isPending}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel Update
          </Button>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Supplier
          </Button>
        </div>
      </div>

      {/* Hidden file input for Excel update */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleExcelUpdate}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading suppliers...</div>
          ) : suppliers && suppliers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Tax ID</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell className="font-medium">
                      {supplier.display_name}
                    </TableCell>
                    <TableCell>{supplier.contact_name || '—'}</TableCell>
                    <TableCell>{supplier.email || '—'}</TableCell>
                    <TableCell>{supplier.phone || '—'}</TableCell>
                    <TableCell>{supplier.tax_id || '—'}</TableCell>
                    <TableCell>{supplier.payment_terms} days</TableCell>
                    <TableCell>
                      <Badge variant={supplier.is_active ? 'default' : 'secondary'}>
                        {supplier.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                     <TableCell>
                       <div className="flex items-center gap-1">
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => handleEdit(supplier)}
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                         <AlertDialog>
                           <AlertDialogTrigger asChild>
                             <Button variant="ghost" size="sm">
                               <Trash2 className="h-4 w-4 text-destructive" />
                             </Button>
                           </AlertDialogTrigger>
                           <AlertDialogContent>
                             <AlertDialogHeader>
                               <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
                               <AlertDialogDescription>
                                 Are you sure you want to delete "{supplier.display_name}"? This action cannot be undone.
                               </AlertDialogDescription>
                             </AlertDialogHeader>
                             <AlertDialogFooter>
                               <AlertDialogCancel>Cancel</AlertDialogCancel>
                               <AlertDialogAction 
                                 onClick={() => deleteSupplierMutation.mutate(supplier.id)}
                                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               >
                                 Delete
                               </AlertDialogAction>
                             </AlertDialogFooter>
                           </AlertDialogContent>
                         </AlertDialog>
                       </div>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No suppliers found. Create your first supplier to get started.
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierForm
        supplier={editingSupplier}
        open={formOpen}
        onOpenChange={handleFormClose}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};